/**
 * Hook for AI Builder execution using WebSocket streaming.
 * 
 * This hook uses the AgentChatConsumer WebSocket for real-time streaming
 * of agent executions, eliminating the need for HTTP polling.
 * 
 * WebSocket URL: <configured WS base>/ws/agents/<agent_id>/chat/
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAgentChatSocket } from './useAgentChatSocket';
import { agentApi } from '../services/api';
import type { ExpertProgress } from './useAISystemSocket';

type ExecutionStartAck = {
  id?: string;
  task_id?: string;
  session_id?: string;
};

export interface UseAIBuilderExecutionOptions {
  /** AI System ID */
  systemId?: number | null;
  /** Agent ID for WebSocket connection */
  agentId: number;
  /** Extra context to include with every execution */
  executionContext?: Record<string, any>;
  /** Whether to auto-connect WebSocket (default: true) */
  autoConnect?: boolean;
  /** Callback when execution completes */
  onExecutionCompleted?: (executionId: string, status: string) => void;
  /** Callback when error occurs */
  onError?: (error: string) => void;
  /** Callback for tool calls */
  onToolCall?: (
    toolName: string,
    toolArgs: Record<string, any>,
    toolCallId?: string,
    data?: Record<string, any>
  ) => void;
  /** Callback for tool results */
  onToolResult?: (toolName: string, result: any, toolCallId?: string) => void;
  /** Callback for public assistant progress/status text */
  onAssistantStatus?: (
    content: string,
    iteration: number,
    data?: {
      execution_id?: string;
      session_id?: string;
      tool_names?: string[];
      tool_group_rationale?: string;
      tool_rationales?: Array<Record<string, any>>;
    }
  ) => void;
  /** Callback for LLM response chunks */
  onLLMResponse?: (content: string, isFinal: boolean) => void;
  /** Callback for nested sub-agent events (source === 'sub_agent'). */
  onSubAgentEvent?: (event: any) => void;
}

export interface UseAIBuilderExecutionReturn {
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Whether an execution is currently running */
  isExecuting: boolean;
  /** Current execution ID */
  currentExecutionId: string | null;
  /** Current queued Celery task ID before an execution row exists */
  currentQueuedTaskId: string | null;
  /** Current session ID from WebSocket */
  currentSessionId: string | null;
  /** Execution status */
  executionStatus: string | null;
  /** Progress percentage (based on iterations) */
  progress: number;
  /** Current iteration */
  currentIteration: number;
  /** Max iterations allowed */
  maxIterations: number;
  /** Expert progress tracking (for compatibility) */
  expertProgress: ExpertProgress[];
  /** Error message */
  error: string | null;
  /** Total cost */
  totalCost: number;
  /** Total tokens used */
  totalTokens: number;
  /** Tool names available */
  toolNames: string[];
  /** Execution result/response */
  executionResult: any | null;
  /** Start execution with message via WebSocket */
  executeWithMessage: (message: string, sessionId?: string | null, context?: Record<string, any>) => Promise<ExecutionStartAck>;
  /** Stop the current execution */
  stopExecution: () => Promise<void>;
  /** Subscribe to an existing execution (for historical sessions) */
  subscribeToExecution: (executionId: string) => void;
  /** Reset state for new conversation */
  reset: () => void;
  /** Connect to WebSocket */
  connect: () => void;
  /** Disconnect from WebSocket */
  disconnect: () => void;
  /** Pending confirmation data for dangerous tools */
  pendingConfirmation: {
    executionId: string;
    toolName: string;
    toolArgs: Record<string, any>;
    toolCallId: string;
    dangerLevel: number;
    message: string;
  } | null;
  /** Confirm or deny a dangerous tool */
  confirmTool: (
    executionId: string,
    toolName: string,
    confirmed: boolean,
    toolCallId?: string | null
  ) => boolean;
}

/**
 * Hook that uses WebSocket streaming for AI Builder agent executions.
 * 
 * Connects to the AgentChatConsumer WebSocket for real-time updates
 * on tool calls, LLM responses, and execution status.
 */
export function useAIBuilderExecution(options: UseAIBuilderExecutionOptions): UseAIBuilderExecutionReturn {
  const {
    systemId,
    agentId,
    executionContext,
    autoConnect = true,
    onExecutionCompleted,
    onError,
    onToolCall,
    onToolResult,
    onAssistantStatus,
    onLLMResponse,
    onSubAgentEvent,
  } = options;

  // Track pending execution promise
  const pendingExecutionRef = useRef<{
    resolve: (value: ExecutionStartAck) => void;
    reject: (error: Error) => void;
  } | null>(null);

  // Track the message being sent (for promise resolution)
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  // Expert progress (for compatibility with existing UI)
  const [expertProgress] = useState<ExpertProgress[]>([]);

  // Polling fallback for when WebSocket is not available
  // NOTE: Defined early so it can be used in socket callbacks
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Track restored execution ID in a ref (to avoid stale closure issues)
  const restoredExecutionIdRef = useRef<string | null>(null);
  
  // Track if we've already sent subscribe for this execution (to avoid duplicates)
  const hasSubscribedRef = useRef<string | null>(null);
  
  // Track completions already handled across stop/reset races.
  const handledCompletionIdsRef = useRef<Set<string>>(new Set());
  
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      console.log('[AIBuilderExecution] Stopping polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Use the agent chat socket
  const socket = useAgentChatSocket({
    agentId,
    autoConnect,
    onConnected: (agentName) => {
      console.log('[AIBuilderExecution] Connected to agent:', agentName);
      
      // Check if the backend auto-subscribed us to a running execution
      // (this happens when the backend detects a running execution on connect)
      const autoSubExecId = socketRef.current.executionId;
      if (autoSubExecId && socketRef.current.isExecuting) {
        console.log('[AIBuilderExecution] Backend auto-subscribed to running execution:', autoSubExecId);
        // The backend already added us to the execution group, so we'll receive
        // real-time WebSocket updates. Stop polling if it was active.
        stopPolling();
        restoredExecutionIdRef.current = autoSubExecId;
        hasSubscribedRef.current = autoSubExecId;
        return;
      }
      
      // If we have a restored execution and haven't subscribed yet, send subscribe message
      if (restoredExecutionIdRef.current && hasSubscribedRef.current !== restoredExecutionIdRef.current) {
        const execId = restoredExecutionIdRef.current;
        console.log('[AIBuilderExecution] Sending subscribe for restored execution:', execId);
        hasSubscribedRef.current = execId; // Mark as subscribed
        // Use setTimeout to ensure socketRef is updated
        setTimeout(() => {
          socketRef.current.sendRaw({
            type: 'subscribe',
            execution_id: execId,
          });
        }, 0);
      }
    },
    onDisconnected: () => {
      console.log('[AIBuilderExecution] Disconnected from agent');
    },
    onReconnected: async (wasExecuting, executionId) => {
      console.log('[AIBuilderExecution] Reconnected! wasExecuting:', wasExecuting, 'executionId:', executionId);
      
      // If we have a restored execution, re-subscribe (need to rejoin group after reconnect)
      if (restoredExecutionIdRef.current) {
        const execId = restoredExecutionIdRef.current;
        console.log('[AIBuilderExecution] Re-subscribing for restored execution on reconnect:', execId);
        // Reset subscription flag since this is a new connection
        hasSubscribedRef.current = execId;
        setTimeout(() => {
          socketRef.current.sendRaw({
            type: 'subscribe',
            execution_id: execId,
          });
        }, 0);
      }
      
      // If we were executing when disconnected (not a restored execution), fetch latest state from API
      if (wasExecuting && executionId) {
        try {
          console.log('[AIBuilderExecution] Fetching execution state from API after reconnect...');
          const execution = await agentApi.getExecutionSummary(executionId);
          
          if (['COMPLETED', 'ERROR', 'CANCELLED', 'FAILED', 'STOPPED'].includes(String(execution.status || '').toUpperCase())) {
            // Execution finished while we were disconnected
            console.log('[AIBuilderExecution] Execution finished while disconnected:', execution.status);
            onExecutionCompleted?.(executionId, execution.status);
          } else {
            console.log('[AIBuilderExecution] Execution still running, re-subscribing after reconnect');
            hasSubscribedRef.current = executionId;
            setTimeout(() => {
              socketRef.current.sendRaw({
                type: 'subscribe',
                execution_id: executionId,
              });
            }, 0);
          }
        } catch (error) {
          console.error('[AIBuilderExecution] Failed to sync execution state:', error);
        }
      }
    },
    onSubscribed: (executionId, status, isStreaming) => {
      console.log('[AIBuilderExecution] Subscribed to execution:', executionId, 'status:', status, 'streaming:', isStreaming);
      
      if (isStreaming) {
        // WebSocket will receive updates, stop polling
        console.log('[AIBuilderExecution] WebSocket streaming available, stopping polling');
        stopPolling();
      } else {
        // No active task in backend, execution might have finished or we need to poll
        console.log('[AIBuilderExecution] No WebSocket streaming, continuing with polling');
      }
      
      if (status !== 'RUNNING') {
        // Execution already finished
        restoredExecutionIdRef.current = null;
        stopPolling();
      }
    },
    onExecutionStarted: (executionId, sessionId) => {
      console.log('[AIBuilderExecution] Execution started:', executionId, 'session:', sessionId);
      // Resolve pending promise when execution starts
      if (pendingExecutionRef.current) {
        pendingExecutionRef.current.resolve({ id: executionId, session_id: sessionId });
        pendingExecutionRef.current = null;
        setPendingMessage(null);
      }
    },
    onExecutionQueued: (taskId, sessionId) => {
      console.log('[AIBuilderExecution] Execution queued:', taskId, 'session:', sessionId);
      if (pendingExecutionRef.current) {
        pendingExecutionRef.current.resolve({
          task_id: taskId,
          session_id: sessionId || undefined,
        });
        pendingExecutionRef.current = null;
        setPendingMessage(null);
      }
    },
    onIterationStarted: (iteration) => {
      console.log('[AIBuilderExecution] Iteration:', iteration);
    },
    onToolCall: (toolName, toolArgs, _iteration, toolCallId, data) => {
      console.log('[AIBuilderExecution] Tool call:', toolName, toolArgs, 'id:', toolCallId);
      onToolCall?.(toolName, toolArgs, toolCallId, data);
    },
    onToolResult: (toolName, result, _iteration, toolCallId) => {
      console.log('[AIBuilderExecution] Tool result:', toolName, 'id:', toolCallId);
      onToolResult?.(toolName, result, toolCallId);
    },
    onAssistantStatus: (content, iteration, data) => {
      console.log('[AIBuilderExecution] Assistant status:', content.substring(0, 80), 'iteration:', iteration);
      onAssistantStatus?.(content, iteration, data);
    },
    onLLMResponse: (content, iteration, isFinal) => {
      console.log('[AIBuilderExecution] LLM response:', content.substring(0, 50) + '...', 'final:', isFinal);
      onLLMResponse?.(content, isFinal);
    },
    onSubAgentEvent: (event) => {
      onSubAgentEvent?.(event);
    },
    onExecutionCompleted: (executionId, status, result) => {
      console.log('[AIBuilderExecution] Execution completed via WebSocket:', executionId, status);
      
      // Check if completion was already handled (to prevent duplicate from polling + WebSocket)
      if (handledCompletionIdsRef.current.has(executionId)) {
        console.log('[AIBuilderExecution] Completion already handled for:', executionId);
        return;
      }
      handledCompletionIdsRef.current.add(executionId);
      
      // Clear the restored execution ref and subscription tracking
      restoredExecutionIdRef.current = null;
      hasSubscribedRef.current = null;
      // Stop polling (in case we were polling for a restored execution)
      stopPolling();
      onExecutionCompleted?.(executionId, status);
    },
    onError: (error) => {
      console.error('[AIBuilderExecution] Error:', error);
      // Reject pending promise on error
      if (pendingExecutionRef.current) {
        pendingExecutionRef.current.reject(new Error(error));
        pendingExecutionRef.current = null;
        setPendingMessage(null);
        return;
      }
      onError?.(error);
    },
  });

  // Keep a ref to always have the latest socket (avoids stale closure issues)
  const socketRef = useRef(socket);
  socketRef.current = socket;

  // Execute via WebSocket
  const executeWithMessage = useCallback(async (
    message: string,
    sessionId?: string | null,
    context?: Record<string, any>
  ): Promise<ExecutionStartAck> => {
    // Access socket through ref to get the latest
    const getSocket = () => socketRef.current;
    
    // Check actual WebSocket connection state (not React state)
    const isActuallyConnected = () => {
      const ws = getSocket().wsRef.current;
      return ws && ws.readyState === WebSocket.OPEN;
    };
    
    // Check if connected using the actual WebSocket ref
    if (!isActuallyConnected()) {
      // Try to connect first (reset reconnect counter in case it was exhausted)
      console.log('[AIBuilderExecution] Not connected, attempting to connect...');
      getSocket().resetAndConnect();
      
      // Wait for connection
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 5000);
        
        const checkConnection = setInterval(() => {
          // Check the actual WebSocket ref for connection status
          if (isActuallyConnected()) {
            clearInterval(checkConnection);
            clearTimeout(timeout);
            resolve();
          }
        }, 100);
      });
    }

    // Final check before executing using actual WebSocket state
    const wsConnected = isActuallyConnected();
    console.log('[AIBuilderExecution] About to execute, wsRef connected:', wsConnected);
    
    if (!wsConnected) {
      throw new Error('WebSocket not connected after waiting');
    }
    
    // Create a promise that will be resolved when execution_started is received
    return new Promise((resolve, reject) => {
      pendingExecutionRef.current = { resolve, reject };
      setPendingMessage(message);

      // Set a timeout for the server acknowledgement. The backend should send
      // execution_queued immediately and execution_started once Celery begins.
      const timeout = setTimeout(() => {
        if (pendingExecutionRef.current) {
          pendingExecutionRef.current.reject(
            new Error('Execution was sent but the server did not acknowledge it within 30 seconds')
          );
          pendingExecutionRef.current = null;
          setPendingMessage(null);
        }
      }, 30000);

      // Build the payload
      const payload: Record<string, any> = {
        type: 'execute',
        message,
      };
      if (sessionId) {
        payload.session_id = sessionId;
      }
      if (systemId) {
        payload.ai_system_id = systemId;
      }
      payload.context = {
        ...executionContext,
        ...context,
      };
      if (systemId) {
        payload.context.system_id = systemId;
      }

      // Send directly using sendRaw to avoid stale closure issues
      console.log('[AIBuilderExecution] Sending execute payload via sendRaw');
      const sent = getSocket().sendRaw(payload);
      
      if (!sent) {
        clearTimeout(timeout);
        pendingExecutionRef.current = null;
        setPendingMessage(null);
        reject(new Error('Failed to send message - WebSocket not connected'));
        return;
      }

      // Clear timeout when promise resolves/rejects
      const originalResolve = pendingExecutionRef.current.resolve;
      const originalReject = pendingExecutionRef.current.reject;
      
      pendingExecutionRef.current.resolve = (value) => {
        clearTimeout(timeout);
        originalResolve(value);
      };
      pendingExecutionRef.current.reject = (error) => {
        clearTimeout(timeout);
        originalReject(error);
      };
    });
  }, [executionContext, systemId]);

  // Stop execution via WebSocket and API (both for reliability)
  const stopExecution = useCallback(async () => {
    const getSocket = () => socketRef.current;
    // Try to get execution ID from socket state, or fall back to our ref
    const execId = getSocket().executionId || restoredExecutionIdRef.current;
    const queuedTaskId = getSocket().queuedTaskId;
    
    if (!execId && queuedTaskId) {
      console.log('[AIBuilderExecution] Cancelling queued execution:', queuedTaskId);
      const sent = getSocket().stop();
      console.log('[AIBuilderExecution] WebSocket queued stop sent:', sent);
      socketRef.current.forceStopState();
      return;
    }

    if (!execId) {
      console.log('[AIBuilderExecution] No execution or queued task to stop');
      return;
    }
    
    console.log('[AIBuilderExecution] Stopping execution:', execId);
    
    // Try WebSocket first
    const sent = getSocket().sendRaw({
      type: 'stop',
      execution_id: execId,
    });
    
    console.log('[AIBuilderExecution] WebSocket stop sent:', sent);
    
    // ALWAYS also call API to ensure stop happens (WebSocket might not respond)
    try {
      console.log('[AIBuilderExecution] Also calling API to stop execution');
      await agentApi.stopExecution(execId);
      console.log('[AIBuilderExecution] Execution stopped via API');
      
      // Check if completion was already handled (WebSocket might have already fired)
      if (handledCompletionIdsRef.current.has(execId)) {
        console.log('[AIBuilderExecution] Stop completion already handled by WebSocket');
        return;
      }
      handledCompletionIdsRef.current.add(execId);
      
      // Clear the restored execution ref
      restoredExecutionIdRef.current = null;
      // Stop any polling
      stopPolling();
      
      // Force stop the socket state immediately (don't wait for WebSocket)
      // This ensures the UI updates even if WebSocket doesn't respond
      socketRef.current.forceStopState();
      
      // Trigger completion callback
      onExecutionCompleted?.(execId, 'CANCELLED');
      
    } catch (error) {
      console.error('[AIBuilderExecution] Failed to stop execution via API:', error);
      onError?.('Failed to stop execution');
    }
  }, [onExecutionCompleted, onError, stopPolling]);
  
  const startPolling = useCallback((execId: string) => {
    // Clear any existing polling
    stopPolling();
    
    console.log('[AIBuilderExecution] Starting polling for execution:', execId);
    
    pollingIntervalRef.current = setInterval(async () => {
      // For restored executions, we MUST poll because WebSocket won't receive updates
      // (the execution was started on a different connection)
      // Don't stop polling just because WebSocket is connected
      
      try {
        const execution = await agentApi.getExecutionSummary(execId);
        
        if (execution.status !== 'RUNNING') {
          console.log('[AIBuilderExecution] Polling detected completion:', execution.status);
          stopPolling();
          
          // Check if completion was already handled (to prevent duplicate from polling + WebSocket)
          if (handledCompletionIdsRef.current.has(execId)) {
            console.log('[AIBuilderExecution] Completion already handled by WebSocket for:', execId);
            return;
          }
          handledCompletionIdsRef.current.add(execId);
          
          // Force stop the socket state since this was a restored execution
          socketRef.current.forceStopState();
          onExecutionCompleted?.(execId, execution.status);
        }
      } catch (error) {
        console.error('[AIBuilderExecution] Polling error:', error);
      }
    }, 2000); // Poll every 2 seconds
  }, [onExecutionCompleted, stopPolling]);

  // Subscribe to existing execution (for page reload scenarios with running executions)
  // The backend now auto-subscribes on reconnect, so WebSocket streaming should work.
  // We still start polling as a fallback in case the backend auto-subscribe didn't happen
  // (e.g. server restarted, execution on different instance).
  const subscribeToExecution = useCallback(async (execId: string) => {
    console.log('[AIBuilderExecution] Subscribe to execution:', execId);
    
    // Fetch current execution status from API
    try {
      const execution = await agentApi.getExecutionSummary(execId);
      
      if (execution.status === 'RUNNING') {
        console.log('[AIBuilderExecution] Execution still running, restoring state');
        
        // Store in ref for immediate access (avoids stale closure issues)
        restoredExecutionIdRef.current = execId;
        
        // Set the socket state directly via ref to indicate we're tracking this execution
        socketRef.current.restore(execId, execution.session_id || null);
        
        // For restored executions, we MUST poll since WebSocket won't receive updates
        // (the execution was started on a different WebSocket connection)
        console.log('[AIBuilderExecution] Starting polling for restored execution');
        startPolling(execId);
      } else {
        console.log('[AIBuilderExecution] Execution already completed:', execution.status);
        onExecutionCompleted?.(execId, execution.status);
      }
    } catch (error) {
      console.error('[AIBuilderExecution] Failed to fetch execution:', error);
      onError?.('Failed to restore execution state');
    }
  }, [onExecutionCompleted, onError, startPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Reset state
  const reset = useCallback(() => {
    console.log('[AIBuilderExecution] Resetting state');
    socketRef.current.reset();
    pendingExecutionRef.current = null;
    restoredExecutionIdRef.current = null;
    hasSubscribedRef.current = null;
    setPendingMessage(null);
    stopPolling();
  }, [stopPolling]);

  // Calculate progress based on current iteration and max iterations
  const progress = socket.currentIteration > 0 
    ? Math.min((socket.currentIteration / socket.maxIterations) * 100, 99) 
    : 0;

  return {
    isConnected: socket.isConnected,
    isExecuting: socket.isExecuting,
    // Return execution ID from socket or from our ref (for restored executions)
    currentExecutionId: socket.executionId || restoredExecutionIdRef.current,
    currentQueuedTaskId: socket.queuedTaskId,
    currentSessionId: socket.sessionId,
    executionStatus: socket.status,
    progress,
    currentIteration: socket.currentIteration,
    maxIterations: socket.maxIterations,
    expertProgress,
    error: socket.error,
    totalCost: socket.totalCost,
    totalTokens: socket.totalTokens,
    toolNames: socket.toolNames,
    executionResult: socket.result,
    executeWithMessage,
    stopExecution,
    subscribeToExecution,
    reset,
    connect: socket.connect,
    disconnect: socket.disconnect,
    pendingConfirmation: socket.pendingConfirmation,
    confirmTool: socket.confirmTool,
  };
}

export default useAIBuilderExecution;
